/**
 * TemplatePrintButton — drop-in replacement for a plain print button.
 * If the tenant has more than one template for the given documentType,
 * shows a picker dialog. Otherwise prints immediately with the default.
 *
 * Props:
 *   documentType  - 'invoice' | 'insurance_invoice' | 'rx' | 'quote' | 'receipt'
 *   onPrint       - (templateId: string|null) => Promise<void>
 *   disabled      - boolean
 *   children      - button label/content (optional)
 *   buttonProps   - extra MUI Button props
 */
import React, { useState, useImperativeHandle } from 'react';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, List, ListItemButton, ListItemText, Chip, Typography
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useQuery } from 'react-query';
import { PrintTemplateService } from 'service/printTemplateService';

const TemplatePrintButton = React.forwardRef(function TemplatePrintButton(
  { documentType, onPrint, disabled, children, buttonProps = {} },
  ref
) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [printing, setPrinting] = useState(false);

  const { data: templatesRes } = useQuery(
    ['printTemplates', documentType],
    () => PrintTemplateService.list(documentType),
    { staleTime: 60_000 }
  );
  const templates = templatesRes?.data || [];
  const tenantTemplates = templates.filter(t => !!t.tenantId);
  const hasChoice = tenantTemplates.length > 1 || (tenantTemplates.length === 1 && templates.some(t => !t.tenantId));

  const doPrint = async (templateId) => {
    setPrinting(true);
    try {
      await onPrint(templateId || null);
    } finally {
      setPrinting(false);
      setDialogOpen(false);
    }
  };

  const handleClick = () => {
    if (hasChoice) {
      setDialogOpen(true);
    } else {
      // No choice — use default (null = server picks default)
      doPrint(null);
    }
  };

  useImperativeHandle(ref, () => ({
    trigger: handleClick,
  }));

  return (
    <>
      <Button
        startIcon={printing ? <CircularProgress size={16} /> : <PrintIcon />}
        onClick={handleClick}
        disabled={disabled || printing}
        {...buttonProps}
      >
        {children || 'Print'}
      </Button>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Print Template</DialogTitle>
        <DialogContent>
          {templates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No templates available.</Typography>
          ) : (
            <List dense disablePadding>
              {templates.map((t) => (
                <ListItemButton key={t.id} onClick={() => doPrint(t.id)}>
                  <ListItemText
                    primary={t.name}
                    secondary={t.description}
                  />
                  {!t.tenantId && <Chip label="System" size="small" color="info" variant="outlined" sx={{ ml: 1 }} />}
                  {t.isDefault && <Chip label="Default" size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => doPrint(null)}>Use Default</Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default TemplatePrintButton;
